"use client";
import React, { useContext, useEffect, useMemo, useState } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { useStoreContext } from "@components/store/provider";

import { useWindowStorage } from "@hooks/use-window-storage";
import useFetchDummyStore from "@hooks/store/use-fetch-dummy-store";
import { getDummyStoreKey, getDummyStoreReturnUrlKey } from "@helpers/store";
import { useSelector } from "react-redux";
import { getStore } from "@selectors/store";

export const CreateShopContext = React.createContext({
  isNewShopCreationFlow: false,
  dummyStoreNumber: "",
});

export const useCreateShopContext = () => useContext(CreateShopContext);

const CreateShopProvider = ({
  children,
  isNewShopCreationFlow: defaultIsNewShopCreationFlow = false,
}) => {
  const { query } = useRouter();
  const { storeNumber } = useStoreContext();

  const { createShop = false } = query;

  const store = useSelector(getStore);
  const [isCreatingNewShop, setIsCreatingNewShop] = useState(defaultIsNewShopCreationFlow);

  const [currentStoreNumber, setCurrentStoreNumber] = useState(store?.number || storeNumber || "");

  const [dummyStoreNumber, setDummyStoreNumber, clearDummyStoreNumber] = useWindowStorage(
    getDummyStoreKey(storeNumber),
    "",
    "sessionStorage",
  );

  const [dummyStoreReturnUrl, setDummyStoreReturnUrl, clearDummyStoreReturnUrl] = useWindowStorage(
    getDummyStoreReturnUrlKey(storeNumber),
    "",
    "sessionStorage",
  );

  const isNewShopCreationFlow = !!createShop || isCreatingNewShop || !!dummyStoreNumber;

  const { fetchDummyStore } = useFetchDummyStore();

  const setDummyStoreParams = ({ number, returnUrl }) => {
    setDummyStoreNumber(number);
    returnUrl && setDummyStoreReturnUrl(returnUrl);
  };

  const clearDummyStoreParams = () => {
    clearDummyStoreReturnUrl();
    clearDummyStoreNumber();
    setIsCreatingNewShop(false);
  };

  useEffect(() => {
    if (createShop && dummyStoreNumber) clearDummyStoreNumber();
  }, [createShop]);

  useEffect(() => {
    if (dummyStoreNumber)
      fetchDummyStore({
        variables: {
          number: dummyStoreNumber,
        },
      });
  }, [dummyStoreNumber]);

  const value = useMemo(
    () => ({
      isNewShopCreationFlow,
      clearDummyStoreParams,
      dummyStoreNumber,
      setDummyStoreParams,
      setIsCreatingNewShop,
      dummyStoreReturnUrl,
      setDummyStoreReturnUrl,
      currentStoreNumber,
      setCurrentStoreNumber,
    }),
    [
      isNewShopCreationFlow,
      clearDummyStoreParams,
      dummyStoreNumber,
      setDummyStoreParams,
      setIsCreatingNewShop,
      dummyStoreReturnUrl,
      setDummyStoreReturnUrl,
      currentStoreNumber,
      setCurrentStoreNumber,
    ],
  );

  return <CreateShopContext.Provider value={value}>{children}</CreateShopContext.Provider>;
};

export default CreateShopProvider;
